
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { payrollAPI } from '../services/api';

type Associate = {
  _id: string;
  name: string;
};

type Payroll = {
  _id: string;
  employeeId: Associate | string;
  month: string;
  salary: number;
  status: string;
  paidDate?: string;
};

const PayrollTab = () => {
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    payrollAPI.getAll()
      .then((res: { data: Payroll[] }) => setPayrolls(res.data))
      .catch((err: unknown) => setError('Failed to fetch payrolls'))
      .finally(() => setLoading(false));
  }, []);

  // Calculate summary
  const totalSalary = payrolls.reduce((sum, p) => sum + (p.salary || 0), 0);
  const monthlyPayroll = payrolls.filter(p => p.month === '2025-08').reduce((sum, p) => sum + (p.salary || 0), 0);
  const annualPayroll = payrolls.reduce((sum, p) => sum + (p.salary || 0), 0); // Simplified for demo

  const navigate = useNavigate();
  return (
    <div className="p-1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
          <span className="text-2xl mb-2">💰</span>
          <div className="text-gray-500 text-sm">Total Salary Expenses</div>
          <div className="text-2xl font-bold mt-1">{totalSalary.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
          <span className="text-2xl mb-2">📅</span>
          <div className="text-gray-500 text-sm">Monthly Payroll Cost</div>
          <div className="text-2xl font-bold mt-1">{monthlyPayroll.toLocaleString()}</div>
          <div className="text-gray-500 text-sm mt-2">Annual Payroll Cost: <span className="font-bold">{annualPayroll.toLocaleString()}</span></div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">All Employee Payrolls</h3>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition"
                onClick={() => navigate('/add-payroll')}
              >
                + Add Payroll
              </button>
            </div>
        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div className="text-red-500">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 rounded-lg text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs">
                  <th className="py-3 px-4 border-b text-left">Employee</th>
                  <th className="py-3 px-4 border-b text-left">Month</th>
                  <th className="py-3 px-4 border-b text-left">Salary</th>
                  <th className="py-3 px-4 border-b text-left">Status</th>
                  <th className="py-3 px-4 border-b text-left">Paid Date</th>
                  <th className="py-3 px-4 border-b text-left">Payslip</th>
                </tr>
              </thead>
              <tbody>
                {payrolls.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-500 py-4">No payrolls found</td>
                  </tr>
                ) : (
                  payrolls.map((p) => (
                    <tr key={p._id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 border-b">
                        <div className="flex flex-col">
                          <span className="text-blue-900 font-medium">{typeof p.employeeId === 'object' ? p.employeeId.name : p.employeeId}</span>
                          <span className="text-xs text-gray-500 mt-1">ID:{typeof p.employeeId === 'object' ? p.employeeId._id : p.employeeId}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 border-b">{p.month}</td>
                      <td className="py-3 px-4 border-b">{p.salary}</td>
                      <td className="py-3 px-4 border-b">{p.status}</td>
                      <td className="py-3 px-4 border-b">{p.paidDate ? new Date(p.paidDate).toLocaleDateString() : '-'}</td>
                      <td className="py-3 px-4 border-b">
                        <button
                          className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition"
                          onClick={() => alert('Payslip generation coming soon!')}
                          disabled={p.status !== 'Paid'}
                        >
                          {p.status === 'Paid' ? 'Download Payslip' : 'Generate Payslip'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollTab;
